import get from 'lodash/get';

import i18n from '@/i18n';

import type { CommonImageItemType } from '../types';

const t = i18n.t.bind(i18n);

/** 制品文件类型（与制品中心 ArtifactFileTypeEnum 取值一致） */
export const IMAGE_PACKAGE_FORMAT = {
  BINARY: 'binary',
  DOCKER: 'docker',
} as const;

export type ImagePackageFormat = (typeof IMAGE_PACKAGE_FORMAT)[keyof typeof IMAGE_PACKAGE_FORMAT];

export interface ImageCardTag {
  label: string;
  value: string;
}

/** 制品类型是否为操作系统镜像 */
export function isSystemImageArtifact(artifactType: string | undefined): boolean {
  return artifactType === 'system_image';
}

function normalizeTagValue(value: unknown): string | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  if (Array.isArray(value)) {
    const joined = value
      .map((item) => String(item ?? '').trim())
      .filter(Boolean)
      .join(',');
    return joined || undefined;
  }
  const text = String(value).trim();
  return text || undefined;
}

/** 按 packageFormat 解析文件大小；未传 format 时优先 docker，再回退 http */
export function resolveImageFileSize(input: {
  packageFormat?: string;
  dockerSourceSize?: string;
  httpSourceSize?: string;
}): string | undefined {
  if (input.packageFormat === IMAGE_PACKAGE_FORMAT.DOCKER) {
    return normalizeTagValue(input.dockerSourceSize);
  }
  if (input.packageFormat === IMAGE_PACKAGE_FORMAT.BINARY) {
    return normalizeTagValue(input.httpSourceSize);
  }
  return normalizeTagValue(input.dockerSourceSize) ?? normalizeTagValue(input.httpSourceSize);
}

export interface ImageCardTagInput {
  artifactType?: string;
  packageFormat?: string;
  vendor?: string;
  cpuArchitectures?: string | string[];
  dockerSourceSize?: string;
  httpSourceSize?: string;
  kernelVersion?: string;
  systemDiskSize?: string | number;
  bootloader?: string;
}

/** 构造镜像卡片 / 摘要共用的展示 Tag 列表 */
export function buildImageCardTags(input: ImageCardTagInput): ImageCardTag[] {
  const tags: ImageCardTag[] = [];

  const vendor = normalizeTagValue(input.vendor);
  if (vendor) {
    tags.push({ label: t('data-center:vendor'), value: vendor });
  }

  const cpuArchitectures = normalizeTagValue(input.cpuArchitectures);
  if (cpuArchitectures) {
    tags.push({ label: t('data-center:supported.cpu.architecture'), value: cpuArchitectures });
  }

  const fileSize = resolveImageFileSize(input);
  if (fileSize) {
    tags.push({ label: t('data-center:file.size'), value: fileSize });
  }

  if (isSystemImageArtifact(input.artifactType)) {
    const systemDiskSize = normalizeTagValue(input.systemDiskSize);
    if (systemDiskSize) {
      tags.push({ label: t('common:system.disk.size'), value: systemDiskSize });
    }

    const kernelVersion = normalizeTagValue(input.kernelVersion);
    if (kernelVersion) {
      tags.push({ label: t('common:kernel.version'), value: kernelVersion });
    }

    if (input.packageFormat === IMAGE_PACKAGE_FORMAT.BINARY) {
      const bootloader = normalizeTagValue(input.bootloader);
      if (bootloader) {
        tags.push({ label: t('common:bootloader'), value: bootloader });
      }
    }
  }

  return tags;
}

/** 从镜像列表项构造卡片 Tag */
export function buildImageCardTagsFromItem(item: CommonImageItemType): ImageCardTag[] {
  return buildImageCardTags({
    artifactType: item.artifactType,
    packageFormat: item.packageFormat,
    vendor: item.vendor,
    cpuArchitectures: item.cpuArchitectures,
    dockerSourceSize: item.dockerSourceSize,
    httpSourceSize: item.httpSourceSize,
    kernelVersion: item.kernelVersion,
    systemDiskSize: get(item, 'options.systemImage.systemVolumeSize'),
    bootloader: get(item, 'options.systemImage.bootloader'),
  });
}
